import type { ItemView, PlanView } from "@tutor/live";
import type { SessionGoal, SkillProgress } from "@tutor/protocol";
import { lazy, Suspense, useId } from "react";
import { useLocale, useT } from "../i18n";
import { skillLabel } from "../insights/skill-labels";
import { stageWords } from "../insights/stages";
import { Brouillon } from "./Brouillon";
import { TeachBack } from "./TeachBack";
import { CHOICE_KEYS, SolutionNote } from "./ItemCard";
import { PromptText, promptSize } from "./PromptText";

// The board (ADR-0052): today's plan along the top and the session's finished exercises
// as pages one tap back. The plan, its ticks and what makes a page are the engine's and
// the log's; this only draws them. Pages are read-only.

const WidgetView = lazy(() =>
  import("../widgets/WidgetView").then((module) => ({ default: module.WidgetView })),
);

type GoalLook = "done" | "current" | "todo" | "setAside";

/** Whether the plan is from today (UTC): yesterday's plan is not shown. */
export function planIsToday(plan: PlanView | null, now: Date = new Date()): plan is PlanView {
  return (
    plan !== null && Math.floor(plan.sessionStart / 1440) === Math.floor(now.getTime() / 86_400_000)
  );
}

function lookOf(goal: SessionGoal, currentSkill: string | null): GoalLook {
  if (goal.state === "done") return "done";
  if (goal.state === "setAside") return "setAside";
  return goal.skillId === currentSkill ? "current" : "todo";
}

/**
 * The plan strip with the page dots. `page` is the page open (an index into `pages`), or
 * `null` for the exercise on the canvas.
 */
export function PlanStrip(props: {
  plan: PlanView | null;
  /** The engine's progress per skill: a climb goal's bar fills toward its target stage. */
  progress?: Readonly<Record<string, SkillProgress>>;
  currentSkill: string | null;
  /** The skill of the exercise on screen, done or not: its goal's bar is its climb. */
  screenSkill?: string | null;
  pages: readonly ItemView[];
  page: number | null;
  onPage: (page: number | null) => void;
}) {
  const { plan, progress = {}, currentSkill, screenSkill = null, pages, page, onPage } = props;
  const t = useT();
  const locale = useLocale();
  const shown = planIsToday(plan) && plan.goals.length > 0;
  if (!shown && pages.length === 0) return null;
  const goals = shown ? plan.goals : [];
  const looks = goals.map((goal) => lookOf(goal, currentSkill));
  // On a phone the strip is one line: the goal being worked on, or the next one to do.
  const current = looks.indexOf("current");
  const focus = current >= 0 ? current : looks.indexOf("todo");
  const focusGoal = goals.at(focus);
  return (
    <nav className="plan-strip" aria-label={t("board.today")}>
      <span className="plan-today">{t("board.today")}</span>
      {goals.length > 0 && (
        <ol className="plan-goals">
          {goals.map((goal, i) => (
            <li
              key={goal.skillId}
              className="plan-goal"
              data-look={looks[i]}
              data-focus={i === focus}
              style={{ ["--fill" as string]: goalFill(goal, progress[goal.skillId]) }}
            >
              <GoalBar
                skillId={goal.skillId}
                progress={goal.skillId === screenSkill ? progress[goal.skillId] : undefined}
              />
              <span className="plan-label">
                {looks[i] === "done" && (
                  <span className="plan-tick" aria-hidden="true">
                    ✓
                  </span>
                )}
                {t(goal.kind === "review" ? "board.goal.review" : "board.goal.climb", {
                  skill: skillLabel(goal.skillId, locale),
                })}
                {looks[i] === "current" && (
                  <GoalStage skillId={goal.skillId} progress={progress[goal.skillId]} />
                )}
                <span className="visually-hidden">
                  {" "}
                  {t(`board.goal.state.${looks[i] ?? "todo"}`)}
                </span>
              </span>
            </li>
          ))}
        </ol>
      )}
      {focusGoal !== undefined && (
        <span className="plan-compact" aria-hidden="true">
          {t("board.goalCount", { n: focus + 1, total: goals.length })} ·{" "}
          {skillLabel(focusGoal.skillId, locale)}
        </span>
      )}
      {pages.length > 0 && (
        <ol className="page-dots" aria-label={t("board.pages")}>
          {pages.map((p, i) => (
            <li key={`${p.itemId}-${String(i)}`}>
              <button
                type="button"
                className="page-dot"
                aria-current={page === i ? "page" : undefined}
                aria-label={t("board.page", { n: i + 1 })}
                onClick={() => {
                  onPage(i);
                }}
              />
            </li>
          ))}
          <li>
            <button
              type="button"
              className="page-dot here"
              aria-current={page === null ? "page" : undefined}
              aria-label={t("board.thisExercise")}
              onClick={() => {
                onPage(null);
              }}
            />
          </li>
        </ol>
      )}
    </nav>
  );
}

/**
 * How far a goal's bar is filled, from the engine's numbers: a done goal is full; a climb
 * goal fills toward its target stage. Drawn only, never written as a number (ADR-0043).
 */
export function goalFill(goal: SessionGoal, progress: SkillProgress | undefined): number {
  if (goal.state === "done") return 1;
  if (goal.kind !== "climb" || progress === undefined || goal.target <= 0) return 0;
  return Math.min(1, Math.max(0, progress.step / goal.target));
}

/**
 * A goal's bar. On the goal of the exercise on screen it is the skill's climb (ADR-0043), named for
 * screen readers like the climb it stands in for; elsewhere it is decoration.
 */
function GoalBar({ skillId, progress }: { skillId: string; progress: SkillProgress | undefined }) {
  const t = useT();
  const locale = useLocale();
  if (progress === undefined) return <span className="plan-bar" aria-hidden="true" />;
  const { label } = stageWords(t, skillLabel(skillId, locale), progress.step, progress.steps);
  return <span className="plan-bar" role="img" aria-label={label} />;
}

/** The stage the child is at on the goal being worked on: « · En route ». */
function GoalStage({
  skillId,
  progress,
}: {
  skillId: string;
  progress: SkillProgress | undefined;
}) {
  const t = useT();
  const locale = useLocale();
  if (progress === undefined) return null;
  const { stage } = stageWords(t, skillLabel(skillId, locale), progress.step, progress.steps);
  return stage === null ? null : <span className="plan-stage"> · {stage}</span>;
}

/** A finished exercise of this session, as it ended: read-only. */
export function BoardPage(props: { page: ItemView; index: number; onBack: () => void }) {
  const { page, index, onBack } = props;
  const t = useT();
  const promptId = useId();
  return (
    <article className="item-card board-page" aria-labelledby={promptId} data-item-id={page.itemId}>
      <p className="board-page-kicker">{t("board.page", { n: index + 1 })}</p>
      <h2 id={promptId} className="item-prompt" data-size={promptSize(page.prompt)}>
        <PromptText text={page.prompt} />
      </h2>
      <ol className="board-tries">
        {page.attempts.map((attempt, i) => (
          <li key={attempt.clientMsgId} data-right={attempt.server === true}>
            {t("board.try", { n: i + 1, answer: shownAnswer(page, attempt.answer) })}{" "}
            <span className="board-try-verdict">
              {t(attempt.server === true ? "board.tryRight" : "board.tryNotYet")}
            </span>
          </li>
        ))}
      </ol>
      {page.solution !== undefined && <SolutionNote item={page} solution={page.solution} />}
      <Brouillon item={page} readOnly />
      <TeachBack item={page} readOnly />
      {page.bench !== undefined && (
        <section className="workbench" aria-label={t("workbench.label")}>
          <Suspense fallback={<div className="workbench-loading" aria-busy="true" />}>
            <WidgetView key={page.bench.source} block={page.bench} />
          </Suspense>
        </section>
      )}
      <button type="button" className="button ghost" onClick={onBack} autoFocus>
        {t("board.backToExercise")}
      </button>
    </article>
  );
}

/** A try as the child gave it: the choice's letter and text for a choice. */
function shownAnswer(page: ItemView, answer: string): string {
  if (answer === "") return "…";
  if (page.answer.kind !== "choice") return answer;
  const index = Number(answer);
  const choice = page.answer.choices[index];
  return choice === undefined ? answer : `${CHOICE_KEYS.charAt(index)}. ${choice}`;
}
